import React, { createContext, useContext, useState, useEffect, useRef } from 'react'
import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  onIdTokenChanged,
  updateProfile,
  sendEmailVerification,
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier
} from 'firebase/auth'
import { auth } from '../firebase'
import { 
  getUserPermissions, 
  autoCreateUserRecord,
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  PERMISSIONS,
  ROLES
} from '../services/permissionService'

const AuthContext = createContext()

async function createSessionCookie(idToken, signal) {
  try {
    const response = await fetch('/api/sessionLogin', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken }),
      signal
    })
    if (!response.ok) {
      console.error('Failed to create session cookie:', response.status)
    }
    return response.ok
  } catch (error) {
    if (error.name !== 'AbortError') {
      console.error('Error creating session cookie:', error)
    }
    return false
  }
}

export function useAuth() {
  return useContext(AuthContext)
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null)
  const [userClaims, setUserClaims] = useState(null)
  const [userPermissions, setUserPermissions] = useState(null)
  const [permissionsLoading, setPermissionsLoading] = useState(false)
  const [permissionsReady, setPermissionsReady] = useState(false)
  const [permissionsError, setPermissionsError] = useState(null)
  const [loading, setLoading] = useState(true)
  const authRequestRef = useRef(0)
  const permissionsRequestRef = useRef(0)
  const sessionRequestRef = useRef(0)
  const sessionAbortControllerRef = useRef(null)

  const isCurrentAuthRequest = (user, requestId) => (
    authRequestRef.current === requestId && auth.currentUser?.uid === user?.uid
  )

  // Parse ID token to get custom claims
  async function parseUserClaims(user, requestId = authRequestRef.current) {
    if (!user) {
      setUserClaims(null)
      setUserPermissions(null)
      setPermissionsReady(true)
      return null
    }

    try {
      const idTokenResult = await user.getIdTokenResult()
      if (!isCurrentAuthRequest(user, requestId)) return null

      const claims = {
        sadmin: idTokenResult.claims.sadmin || false,
        customer: idTokenResult.claims.customer || '',
        cc: idTokenResult.claims.cc || false,
        layout: idTokenResult.claims.layout || false,
        // Add any other custom claims as needed
        ...idTokenResult.claims
      }
      setUserClaims(claims)
      
      // Load granular permissions after claims are set
      await loadUserPermissions(user, claims, requestId)
      
      return claims
    } catch (error) {
      if (!isCurrentAuthRequest(user, requestId)) return null

      console.error('Error parsing user claims:', error)
      setUserClaims(null)
      setUserPermissions(null)
      setPermissionsError('We could not verify your account access. Please try again.')
      setPermissionsReady(true)
      setPermissionsLoading(false)
      return null
    }
  }

  // Load user permissions from {customer}_users collection
  async function loadUserPermissions(user, claims = null, authRequestId = authRequestRef.current) {
    const permissionsRequestId = ++permissionsRequestRef.current
    if (!user || !isCurrentAuthRequest(user, authRequestId)) return null

    const isCurrent = () => (
      isCurrentAuthRequest(user, authRequestId) &&
      permissionsRequestRef.current === permissionsRequestId
    )

    try {
      setPermissionsLoading(true)
      setPermissionsReady(false)
      setPermissionsError(null)
      const userClaimsToUse = claims || userClaims

      // Super admin - set special permissions
      if (userClaimsToUse?.sadmin) {
        const superAdminPermissions = {
          isSuperAdmin: true,
          roles: [ROLES.SUPER_ADMIN],
          permissions: Object.values(PERMISSIONS).flat(),
          status: 'active'
        }
        if (isCurrent()) {
          setUserPermissions(superAdminPermissions)
        }
        return superAdminPermissions
      }

      // Get customer from claims
      const customer = userClaimsToUse?.customer
      if (!customer) {
        if (isCurrent()) {
          setUserPermissions(null)
          setPermissionsError('Your account is not linked to a customer workspace.')
        }
        return null
      }

      // Load granular permissions
      let permissions = await getUserPermissions(customer, user.email)
      if (!isCurrent()) return null
      
      // Auto-create user record if doesn't exist (for claim holders)
      if (!permissions) {
        const created = await autoCreateUserRecord(customer, user.email, {
          name: user.displayName || user.email.split('@')[0]
        })
        if (!isCurrent()) return null
        
        if (created) {
          permissions = await getUserPermissions(customer, user.email)
          if (!isCurrent()) return null
        }
      }

      if (isCurrent()) {
        setUserPermissions(permissions)
        setPermissionsError(
          permissions ? null : 'We could not load your account access. Please try again.'
        )
      }
      return permissions
    } catch (error) {
      if (!isCurrent()) return null

      console.error('Error loading user permissions:', error)
      setUserPermissions(null)
      setPermissionsError('We could not load your account access. Please try again.')
      return null
    } finally {
      if (isCurrent()) {
        setPermissionsLoading(false)
        setPermissionsReady(true)
      }
    }
  }

  // Permission checking functions
  function checkPermission(permission) {
    if (!currentUser) return false
    
    // Super admin has all permissions
    if (userClaims?.sadmin) {
      return true
    }
    
    // For customer users, create a user object with claims
    const userWithClaims = {
      ...currentUser,
      customClaims: userClaims
    }
    
    return hasPermission(userWithClaims, userPermissions, permission)
  }

  function checkAnyPermission(permissions) {
    if (!currentUser) return false
    
    // Super admin has all permissions
    if (userClaims?.sadmin) {
      return true
    }
    
    // For customer users, create a user object with claims
    const userWithClaims = {
      ...currentUser,
      customClaims: userClaims
    }
    
    return hasAnyPermission(userWithClaims, userPermissions, permissions)
  }

  function checkAllPermissions(permissions) {
    if (!currentUser) return false
    
    // Super admin has all permissions  
    if (userClaims?.sadmin) {
      return true
    }
    
    // For customer users, create a user object with claims
    const userWithClaims = {
      ...currentUser,
      customClaims: userClaims
    }
    
    return hasAllPermissions(userWithClaims, userPermissions, permissions)
  }

  // Refresh permissions (useful after role changes)
  async function refreshPermissions() {
    if (currentUser) {
      await loadUserPermissions(currentUser, userClaims)
    }
  }

  // Force refresh permissions (clears cache and reloads)
  async function forceRefreshPermissions() {
    if (currentUser) {
      await loadUserPermissions(currentUser, userClaims)
    }
  }

  // Register new user
  async function signup(email, password, displayName) {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password)
      
      // Update user profile with display name
      if (displayName) {
        await updateProfile(result.user, {
          displayName: displayName
        })
      }
      
      // Send email verification
      await sendEmailVerification(result.user)
      
      return result
    } catch (error) {
      throw error
    }
  }

  // Login user with MFA support
  async function login(email, password) {
    try {
      return await signInWithEmailAndPassword(auth, email, password)
    } catch (error) {
      // Check if MFA is required
      if (error.code === 'auth/multi-factor-auth-required') {
        // Return the error with resolver for MFA handling
        error.resolver = multiFactor(error.user || auth.currentUser)
        throw error
      }
      throw error
    }
  }

  // Resolve MFA challenge
  async function resolveMFAChallenge(resolver, verificationCode, phoneNumber) {
    try {
      const phoneAuthProvider = new PhoneAuthProvider(auth)
      const phoneInfoOptions = {
        multiFactorHint: resolver.hints[0],
        session: resolver.session
      }
      
      const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions)
      const cred = PhoneAuthProvider.credential(verificationId, verificationCode)
      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred)
      
      return await resolver.resolveSignIn(multiFactorAssertion)
    } catch (error) {
      throw error
    }
  }

  async function logout() {
    try {
      await fetch('/api/sessionLogout', { method: 'POST', credentials: 'include' }).catch(() => {})
      return await signOut(auth)
    } catch (error) {
      throw error
    }
  }

  // Reset password
  async function resetPassword(email) {
    try {
      return await sendPasswordResetEmail(auth, email)
    } catch (error) {
      throw error
    }
  }

  // Enable MFA (SMS) with reCAPTCHA
  async function enableMFA(phoneNumber) {
    try {
      // Create reCAPTCHA verifier for phone auth
      if (!window.recaptchaVerifier) {
        window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
          size: 'invisible',
          callback: (response) => {
            // reCAPTCHA solved
            console.log('reCAPTCHA verified');
          },
          'expired-callback': () => {
            console.log('reCAPTCHA expired');
          }
        });
      }

      const multiFactorSession = await multiFactor(currentUser).getSession()
      const phoneInfoOptions = {
        phoneNumber: phoneNumber,
        session: multiFactorSession
      }
      
      const phoneAuthProvider = new PhoneAuthProvider(auth)
      const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, window.recaptchaVerifier)
      
      return verificationId
    } catch (error) {
      // Clear the reCAPTCHA verifier on error
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
      }
      throw error
    }
  }

  // Verify MFA code
  async function verifyMFACode(verificationId, verificationCode) {
    try {
      const cred = PhoneAuthProvider.credential(verificationId, verificationCode)
      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred)
      
      await multiFactor(currentUser).enroll(multiFactorAssertion, 'SMS MFA')
      return true
    } catch (error) {
      throw error
    }
  }

  // Get user MFA status
  function getMFAStatus() {
    if (!currentUser) return null
    return {
      isEnabled: currentUser.multiFactor?.enrolledFactors?.length > 0 || false,
      enrolledFactors: currentUser.multiFactor?.enrolledFactors || []
    }
  }

  useEffect(() => {
    let sessionUserId = null

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      const requestId = ++authRequestRef.current
      permissionsRequestRef.current += 1
      sessionRequestRef.current += 1
      sessionAbortControllerRef.current?.abort()
      sessionAbortControllerRef.current = null

      setCurrentUser(user)
      setUserClaims(null)
      setUserPermissions(null)
      setPermissionsError(null)
      setPermissionsReady(!user)
      setPermissionsLoading(Boolean(user))

      if (!user) {
        sessionUserId = null
        setLoading(false)
        return
      }

      try {
        if (sessionUserId !== user.uid) {
          const sessionRequestId = ++sessionRequestRef.current
          const controller = new AbortController()
          sessionAbortControllerRef.current = controller

          const token = await user.getIdToken()
          if (
            !isCurrentAuthRequest(user, requestId) ||
            sessionRequestRef.current !== sessionRequestId
          ) return

          await createSessionCookie(token, controller.signal)
          if (
            !isCurrentAuthRequest(user, requestId) ||
            sessionRequestRef.current !== sessionRequestId
          ) return

          sessionUserId = user.uid
          if (sessionAbortControllerRef.current === controller) {
            sessionAbortControllerRef.current = null
          }
        }

        await parseUserClaims(user, requestId)
      } catch (error) {
        if (!isCurrentAuthRequest(user, requestId)) return

        console.error('Error restoring authenticated session:', error)
        setUserClaims(null)
        setUserPermissions(null)
        setPermissionsError('We could not restore your account access. Please try again.')
        setPermissionsLoading(false)
        setPermissionsReady(true)
      } finally {
        if (isCurrentAuthRequest(user, requestId)) {
          setLoading(false)
        }
      }
    })

    const unsubscribeToken = onIdTokenChanged(auth, async (user) => {
      if (user && sessionUserId === user.uid) {
        const sessionRequestId = ++sessionRequestRef.current
        sessionAbortControllerRef.current?.abort()
        const controller = new AbortController()
        sessionAbortControllerRef.current = controller

        try {
          const token = await user.getIdToken()
          if (
            auth.currentUser?.uid === user.uid &&
            sessionRequestRef.current === sessionRequestId
          ) {
            await createSessionCookie(token, controller.signal)
          }
        } catch (error) {
          console.error('Error refreshing session cookie:', error)
        } finally {
          if (sessionAbortControllerRef.current === controller) {
            sessionAbortControllerRef.current = null
          }
        }
      }
    })

    return () => {
      authRequestRef.current += 1
      permissionsRequestRef.current += 1
      sessionRequestRef.current += 1
      sessionAbortControllerRef.current?.abort()
      unsubscribe()
      unsubscribeToken()
    }
  }, [])

  const value = {
    // Authentication
    currentUser,
    userClaims,
    signup,
    login,
    logout,
    resetPassword,
    enableMFA,
    verifyMFACode,
    getMFAStatus,
    parseUserClaims,
    resolveMFAChallenge,
    
    // Permissions
    userPermissions,
    permissionsLoading,
    permissionsReady,
    permissionsError,
    hasPermission: checkPermission,
    hasAnyPermission: checkAnyPermission,
    hasAllPermissions: checkAllPermissions,
    refreshPermissions,
    forceRefreshPermissions,
    
    // Permission constants
    PERMISSIONS,
    ROLES,
    
    // Computed values for convenience
    isSuperAdmin: userClaims?.sadmin || false,
    customer: userClaims?.customer || null,
    isCustomerAdmin: userPermissions?.roles?.includes(ROLES.CUSTOMER_ADMIN) || false,
    loading
  }

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  )
}
